import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";

/**
 * Menu contextual de três pontos (DEC-112). Os itens são botões com `data-act` (os mesmos das ações em linha) e só existem enquanto o menu
 * está aberto. Posição fixa calculada a partir do botão, para não ser cortado pela rolagem da tabela. Fecha com Esc, clique fora ou ao escolher um item.
 */
export function RowMenu({ label, children }: { label: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);

  // posição calculada ANTES da pintura (o menu nunca aparece fora da tela, nem rola a página para alcançá-lo)
  useLayoutEffect(() => {
    if (!open) { setPos(null); return; }
    const r = btn.current?.getBoundingClientRect();
    if (r) {
      const w = 230, h = menu.current?.offsetHeight ?? 260;
      const left = Math.max(8, Math.min(window.innerWidth - w - 8, r.right - w));
      const top = r.bottom + h + 8 > window.innerHeight ? Math.max(8, r.top - h - 4) : r.bottom + 4;
      setPos({ top, left });
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (!menu.current?.contains(e.target as Node) && !btn.current?.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { setOpen(false); btn.current?.focus(); } };
    // fecha ao rolar/redimensionar SÓ se o botão realmente se moveu (um scroll atrasado, disparado antes da abertura, não deve fechar o menu)
    const at = btn.current?.getBoundingClientRect();
    const close = (e?: Event) => {
      if (e && e.target instanceof Node && menu.current?.contains(e.target)) return;
      const now = btn.current?.getBoundingClientRect();
      if (at && now && Math.abs(now.top - at.top) < 4 && Math.abs(now.left - at.left) < 4) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDown); document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", close, true); window.addEventListener("resize", close);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); window.removeEventListener("scroll", close, true); window.removeEventListener("resize", close); };
  }, [open]);

  return (
    <span className="row-menu">
      <button ref={btn} type="button" className="menu-btn" aria-haspopup="menu" aria-expanded={open} aria-label={`Ações: ${label}`} title="Ações" data-row-menu onClick={() => setOpen((o) => !o)}>⋮</button>
      {open && (
        <div ref={menu} className="menu-pop" role="menu" style={{ top: pos?.top ?? -9999, left: pos?.left ?? -9999 }} onClick={() => setOpen(false)}>
          {children}
        </div>
      )}
    </span>
  );
}
